import { useState, useEffect } from 'react';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const FollowButton = ({ entityType, entityId, entityName, size = 'default', className = '' }) => {
  const { user, token } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useEffect(() => {
    fetchFollowStatus();
    fetchFollowersCount();
  }, [entityId, token]);

  const fetchFollowStatus = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/following`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const following = response.data[`${entityType}s`] || response.data[entityType === 'business' ? 'businesses' : `${entityType}s`] || [];
      setIsFollowing(following.some(e => e.id === entityId));
    } catch (error) {
      // Ignore
    }
  };

  const fetchFollowersCount = async () => {
    try {
      const response = await axios.get(`${API}/followers/${entityType}/${entityId}`);
      setFollowersCount(response.data.followers_count);
    } catch (error) {
      // Ignore
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error('Please log in to follow artists');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/follow/${entityType}/${entityId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFollowing(response.data.following);
      setFollowersCount(prev => response.data.following ? prev + 1 : prev - 1);
      toast.success(response.data.following ? `Following ${entityName}` : `Unfollowed ${entityName}`);
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'h-8 text-xs px-3',
    default: 'h-10 px-4',
    lg: 'h-12 text-lg px-6'
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        onClick={handleFollow}
        disabled={loading}
        variant={isFollowing ? 'outline' : 'default'}
        className={sizeClasses[size]}
        data-testid={`follow-btn-${entityId}`}
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : isFollowing ? (
          <>
            <UserMinus size={16} className="mr-2" />
            Following
          </>
        ) : (
          <>
            <UserPlus size={16} className="mr-2" />
            Follow
          </>
        )}
      </Button>
      {followersCount > 0 && (
        <span className="text-sm text-muted-foreground">
          {followersCount.toLocaleString()} follower{followersCount !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
};

export default FollowButton;
